import { useState } from 'react'
import { Pencil } from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useViewOnly } from '@/components/templates/app-shell'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { patchJSON } from '@/lib/api'
import { apiFieldMessages } from '@/lib/contacts/errors'
import { displayDate } from '@/lib/dates'
import { InvoiceCategorySelect } from '@/pages/InvoiceCategorySelect'
import { isConnectionOwned } from '@/pages/OwnedInvoiceProvenance'
import type { Invoice, InvoiceResponse, ManualInvoiceOrigin } from '@/pages/InvoicesPage.types'

export function InvoiceDetailsCard({ invoice }: { invoice: Invoice }) {
  const viewOnly = useViewOnly()
  return (
    <section className="px-4 py-3">
      <h2 className="pb-2 text-body font-semibold text-foreground">Details</h2>
      <dl className="grid grid-cols-[max-content_1fr] items-center gap-x-6 gap-y-0">
        <DetailRow label="Invoice number" value={String(invoice.number)} />
        <DetailRow label="Billed to" value={invoice.contact.name} />
        <DetailRow label="Issue date" value={displayDate(invoice.issue_on)} />
        <DetailRow label="Total" value={invoice.total} />
        <div className="col-span-2 my-1 border-t border-border" />
        <CategoryField invoice={invoice} viewOnly={viewOnly} />
        <PersonalPurchaseField invoice={invoice} viewOnly={viewOnly} />
      </dl>
      <p id="invoice-personal-purchase-help" className="pt-1 text-caption text-muted-foreground">
        {PERSONAL_PURCHASE_HELP}
      </p>
    </section>
  )
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="flex h-9 items-center text-body-sm text-muted-foreground">{label}</dt>
      <dd className="flex h-9 items-center text-body-sm font-medium text-foreground">{value}</dd>
    </>
  )
}

function useInvoiceFact(invoice: Invoice, field: 'origin' | 'personal_purchase') {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (value: string | boolean) =>
      patchJSON<InvoiceResponse>(`/api/v1/invoices/${invoice.id}/${field}`, {
        invoice: { [field]: value },
      }),
    onSuccess: async (data) => {
      queryClient.setQueryData(['invoice', invoice.id], data)
      await queryClient.invalidateQueries({ queryKey: ['invoices'] })
    },
  })
}

function CategoryField({ invoice, viewOnly }: { invoice: Invoice; viewOnly: boolean }) {
  const [editing, setEditing] = useState(false)
  const change = useInvoiceFact(invoice, 'origin')
  const editableOrigin = ['Manual', 'Sponsorship', 'Training'].includes(invoice.origin)
  const editable = !viewOnly && !isConnectionOwned(invoice) && editableOrigin
  return (
    <>
      <dt className="flex h-9 items-center text-body-sm text-muted-foreground">Category</dt>
      <dd className="flex h-9 items-center text-body-sm font-medium text-foreground">
        {editing ? (
          <InvoiceCategorySelect
            value={invoice.origin.toLowerCase() as ManualInvoiceOrigin}
            defaultOpen
            onClose={() => setEditing(false)}
            disabled={change.isPending}
            onChange={(value) => {
              setEditing(false)
              change.mutate(value)
            }}
          />
        ) : (
          <>
            {invoice.category_label}
            {editable && (
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="ml-1 text-muted-foreground"
                      aria-label="Change category"
                      disabled={change.isPending}
                      onClick={() => {
                        change.reset()
                        setEditing(true)
                      }}
                    >
                      <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Change category</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </>
        )}
      </dd>
      <FactError error={change.error} field="origin" />
    </>
  )
}

// Personal Purchase reach (ADR 0065, amended 2026-10-02): the API leaves everything bought on a
// flagged invoice out of every organization's figures; activity with no invoice has nothing to flag.
const PERSONAL_PURCHASE_HELP =
  "Leaves this invoice, its refunds and the places, membership and enrolments bought on it out of every organization's figures. Activity with no invoice, such as a free place or an imported enrolment, can't be hidden."

function PersonalPurchaseField({ invoice, viewOnly }: { invoice: Invoice; viewOnly: boolean }) {
  const change = useInvoiceFact(invoice, 'personal_purchase')
  return (
    <>
      <dt className="flex h-9 items-center">
        <Label htmlFor="invoice-personal-purchase" className="text-body-sm text-muted-foreground">
          Personal purchase
        </Label>
      </dt>
      <dd className="flex h-9 items-center">
        <Checkbox
          id="invoice-personal-purchase"
          aria-describedby="invoice-personal-purchase-help"
          checked={invoice.personal_purchase ?? false}
          disabled={viewOnly || change.isPending}
          onCheckedChange={(checked) => change.mutate(checked === true)}
        />
      </dd>
      <FactError error={change.error} field="personal_purchase" />
    </>
  )
}

function FactError({ error, field }: { error: Error | null; field: string }) {
  if (!error) return null
  return (
    <div className="col-span-2 py-2">
      <Alert>{apiFieldMessages(error, field)[0] ?? error.message}</Alert>
    </div>
  )
}
